import React from 'react';
import { Box, Stack, Tooltip } from '@mui/material';

import type { BaseStepType, IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { BaseStepIcon } from '../components';
import { getStepConfig } from '../getStepConfig';
import { determineIfStepIsDisabled, getStatusColour } from '../utils';
import { usePatientIntakeStatusContext, useStepEdit } from '../hooks';

type TooltipContentProps<TBaseStepType extends BaseStepType> = {
  step: IntakeStatusBaseSteps[TBaseStepType];
};

const TooltipContent = <TStepKey extends keyof IntakeStatusBaseSteps>(props: TooltipContentProps<TStepKey>) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();

  const stepConfig = getStepConfig(props.step.id);
  const { label, context } = stepConfig.statuses[props.step.currentStatus.status];

  const stepStatusContext = context({
    isHealthcareProvider,
  });

  return (
    <div>
      {label} - {stepStatusContext}
    </div>
  );
};

type StepIndicatorProps<TBaseStepType extends BaseStepType> = {
  step: IntakeStatusBaseSteps[TBaseStepType];
};

const StepIndicator = <TBaseStepType extends BaseStepType>({ step }: StepIndicatorProps<TBaseStepType>) => {
  const { setEditingStep } = useStepEdit();
  const { main } = getStatusColour(step.currentStatus.status);

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    setEditingStep(step.id);
  };

  return (
    <Tooltip title={<TooltipContent step={step} />} arrow placement="top">
      <Box
        onClick={handleOnClick}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '50%',
          borderWidth: '2px',
          borderStyle: 'solid',
          color: main,
          borderColor: main,
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          animation: `slideInFromLeft 0.5s ease-in-out`,
          '&:hover': {
            '& .MuiSvgIcon-root': {
              fontSize: 14,
            },
          },
        }}
      >
        <BaseStepIcon step={step} sx={{ fontSize: 12 }} noTooltip />
      </Box>
    </Tooltip>
  );
};

interface BaseStepsStatusIndicatorProps extends React.ComponentProps<typeof Stack> {}

export const BaseStepsStatusIndicator: React.FC<BaseStepsStatusIndicatorProps> = (props) => {
  const { baseSteps } = usePatientIntakeStatusContext();

  if (!baseSteps) {
    return null;
  }

  const visibleSteps = Object.entries(baseSteps).filter(([_, step]) => !determineIfStepIsDisabled({ step }));

  return (
    <Stack
      direction="row"
      gap={1}
      {...props}
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        ...props.sx,
      }}
    >
      {visibleSteps.map(([stepKey, step]) => (
        <StepIndicator key={stepKey} step={step} />
      ))}
    </Stack>
  );
};
